import React, { useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from 'react-router-dom';
import {
    SlidersVertical,
    Piano,
    Drum,
    Table,
    User,
    ArrowLeft,
    Download,
    RotateCcw
} from 'lucide-react';
import { FileUploadArea } from '../components/catalogue/FileUploadArea';
import { FileUploadState } from '../types/project';
import { API_CONFIG, getAuthHeadersWithoutCredentials } from '../constants/api';
import toast from 'react-hot-toast';
// import { useWebSocket } from '../hooks/useWebSocket';
import { SoundSignalVisualizer } from '../components/ui/SoundSignalVisualizer';
import { useCredits } from '../contexts/CreditsContext';

interface AudioTool {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
}

const audioTools: AudioTool[] = [
    {
        id: 'mastering',
        title: 'Track mastering',
        description: 'Make your track sound more professional with our automated mastering service.',
        icon: <SlidersVertical size={ 24 } />
    },
    {
        id: 'instrument-isolation',
        title: 'Instrument Isolation',
        description: 'Isolate individual instrumental and vocal stems from your track.',
        icon: <Piano size={ 24 } />
    },
    {
        id: 'drum-isolation',
        title: 'Drum Isolation',
        description: 'Separate your drum stem further into kicks, snares, hihats and toms.',
        icon: <Drum size={ 24 } />
    },
    {
        id: 'chord-detector',
        title: 'Chord Detector',
        description: 'Get the key and chord progression for any song.',
        icon: <Table size={ 24 } />
    },
    // {
    //     id: 'vocal-cleanup',
    //     title: 'Vocal Clean-up',
    //     description: 'Put a vocal stem in here and remove the noise.',
    //     icon: <AudioWaveform size={ 24 } />
    // },
    // {
    //     id: 'vocal-isolation',
    //     title: 'Vocal Isolation',
    //     description: 'This is going to be the final version. Make it really pop with our automastering solution.',
    //     icon: <MicVocal size={ 24 } />
    // }
];

export const AudioUtilitiesPage: React.FC = () => {
    const { isAuthenticated, isLoading } = useAuth0();
    const navigate = useNavigate();
    const username = localStorage.getItem("name") || "User";
    const { credits, refreshCredits } = useCredits();
    // const { isConnected: isWebSocketConnected } = useWebSocket();
    const [selectedTool, setSelectedTool] = useState<string | null>(null);
    const [fileUploadState, setFileUploadState] = useState<FileUploadState>({
        selectedFile: null,
        acknowledged: false,
        loading: false,
        projectId: null
    });
    const [masteringJobId, setMasteringJobId] = useState<string | null>(null);
    const [masteredTrackUrl, setMasteredTrackUrl] = useState<string | null>(null);
    const [isMasteringComplete, setIsMasteringComplete] = useState(false);
    const [chordTranscriptionResult, setChordTranscriptionResult] = useState<string | null>(null);
    const [chordTranscriptionName, setChordTranscriptionName] = useState<string | null>(null);
    const [isChordTranscriptionComplete, setIsChordTranscriptionComplete] = useState(false);
    const [stemSeparationResult, setStemSeparationResult] = useState<{
        Bass?: string;
        Keys?: string;
        Wind?: string;
        Drums?: string;
        Piano?: string;
        Vocals?: string;
        Guitars?: string;
        Strings?: string;
    } | null>(null);
    const [stemSeparationName, setStemSeparationName] = useState<string | null>(null);
    const [isStemSeparationComplete, setIsStemSeparationComplete] = useState(false);
    const [drumStemsResult, setDrumStemsResult] = useState<{
        Kick?: string;
        Snare?: string;
        HiHat?: string;
        Tom?: string;
        Cymbal?: string;
    } | null>(null);
    const [drumStemsName, setDrumStemsName] = useState<string | null>(null);
    const [isDrumStemsComplete, setIsDrumStemsComplete] = useState(false);

    useEffect(() => {
        if (!isAuthenticated && !isLoading) {
            navigate('/');
            return;
        }
    }, [isAuthenticated, isLoading, navigate]);

    const handleToolSelect = (toolId: string) => {
        console.log('Tool selected:', toolId);
        setSelectedTool(toolId);
    };

    const handleBackToTools = () => {
        setSelectedTool(null);
        setFileUploadState({
            selectedFile: null,
            acknowledged: false,
            loading: false,
            projectId: null
        });
        setMasteringJobId(null);
        setMasteredTrackUrl(null);
        setIsMasteringComplete(false);
        setChordTranscriptionResult(null);
        setIsChordTranscriptionComplete(false);
        setStemSeparationResult(null);
        setIsStemSeparationComplete(false);
        setDrumStemsResult(null);
        setIsDrumStemsComplete(false);
    };

    const handleFileSelect = (file: File) => {
        setFileUploadState(prev => ({
            ...prev,
            selectedFile: file
        }));
    };

    const handleFileDrop = (file: File) => {
        setFileUploadState(prev => ({
            ...prev,
            selectedFile: file
        }));
    };

    const handleAcknowledgedChange = (acknowledged: boolean) => {
        setFileUploadState(prev => ({
            ...prev,
            acknowledged
        }));
    };

    const handleGenerate = () => {
        if (!fileUploadState.selectedFile) return;

        setFileUploadState(prev => ({
            ...prev,
            loading: true
        }));

        if (selectedTool === 'mastering') {
            callMasteringEndpoint();
        } else if (selectedTool === 'chord-detector') {
            callChordTranscriptionEndpoint();
        } else if (selectedTool === 'instrument-isolation') {
            callStemSeparationEndpoint();
        } else if (selectedTool === 'drum-isolation') {
            callDrumStemsEndpoint();
        }
    };

    const callMasteringEndpoint = async () => {
        if (!fileUploadState.selectedFile) return;

        // if (!isWebSocketConnected) {
        //     toast.error('WebSocket connection is not available. Please check your connection and try again.');
        //     return;
        // }

        try {
            const formData = new FormData();
            formData.append('file', fileUploadState.selectedFile);

            const sessionId = localStorage.getItem('session_id');
            const params = new URLSearchParams();
            if (sessionId) {
                params.append('session_id', sessionId);
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUDIO_MASTERING}${params.toString() ? `?${params.toString()}` : ''}`, {
                method: 'POST',
                headers: {
                    'Authorization': getAuthHeadersWithoutCredentials().Authorization,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Failed to start mastering process');
            }

            const result = await response.json();

            if (result.job_id) {
                setMasteringJobId(result.job_id);
            }

            toast.success('Mastering process started! Waiting for completion...');
        } catch (error) {
            console.error('Error calling mastering endpoint:', error);
            toast.error('Failed to start mastering process');
            setFileUploadState(prev => ({
                ...prev,
                loading: false
            }));
        }
    };

    const callChordTranscriptionEndpoint = async () => {
        if (!fileUploadState.selectedFile) return;

        // if (!isWebSocketConnected) {
        //     toast.error('WebSocket connection is not available. Please check your connection and try again.');
        //     return;
        // }

        try {
            const formData = new FormData();
            formData.append('file', fileUploadState.selectedFile);

            const sessionId = localStorage.getItem('session_id');
            const params = new URLSearchParams();
            if (sessionId) {
                params.append('session_id', sessionId);
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUDIO_TRANSCRIBE_CHORDS}${params.toString() ? `?${params.toString()}` : ''}`, {
                method: 'POST',
                headers: {
                    'Authorization': getAuthHeadersWithoutCredentials().Authorization,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Failed to start chord transcription process');
            }

            const result = await response.json();

            if (result.job_id) {
                setMasteringJobId(result.job_id);
            }

            toast.success('Chord transcription process started! Waiting for completion...');
        } catch (error) {
            console.error('Error calling chord transcription endpoint:', error);
            toast.error('Failed to start chord transcription process');
            setFileUploadState(prev => ({
                ...prev,
                loading: false
            }));
        }
    };

    const callStemSeparationEndpoint = async () => {
        if (!fileUploadState.selectedFile) return;

        // if (!isWebSocketConnected) {
        //     toast.error('WebSocket connection is not available. Please check your connection and try again.');
        //     return;
        // }

        try {
            const formData = new FormData();
            formData.append('file', fileUploadState.selectedFile);

            const sessionId = localStorage.getItem('session_id');
            const params = new URLSearchParams();
            if (sessionId) {
                params.append('session_id', sessionId);
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUDIO_STEM_SEPARATION}${params.toString() ? `?${params.toString()}` : ''}`, {
                method: 'POST',
                headers: {
                    'Authorization': getAuthHeadersWithoutCredentials().Authorization,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Failed to start stem separation process');
            }

            const result = await response.json();

            if (result.job_id) {
                setMasteringJobId(result.job_id);
            }

            toast.success('Stem separation process started! Waiting for completion...');
        } catch (error) {
            console.error('Error calling stem separation endpoint:', error);
            toast.error('Failed to start stem separation process');
            setFileUploadState(prev => ({
                ...prev,
                loading: false
            }));
        }
    };

    const callDrumStemsEndpoint = async () => {
        if (!fileUploadState.selectedFile) return;

        // if (!isWebSocketConnected) {
        //     toast.error('WebSocket connection is not available. Please check your connection and try again.');
        //     return;
        // }

        try {
            const formData = new FormData();
            formData.append('file', fileUploadState.selectedFile);

            const sessionId = localStorage.getItem('session_id');
            const params = new URLSearchParams();
            if (sessionId) {
                params.append('session_id', sessionId);
            }

            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUDIO_DRUM_STEMS}${params.toString() ? `?${params.toString()}` : ''}`, {
                method: 'POST',
                headers: {
                    'Authorization': getAuthHeadersWithoutCredentials().Authorization,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Failed to start drum stems process');
            }

            const result = await response.json();

            if (result.job_id) {
                setMasteringJobId(result.job_id);
            }

            toast.success('Drum stems process started! Waiting for completion...');
        } catch (error) {
            console.error('Error calling drum stems endpoint:', error);
            toast.error('Failed to start drum stems process');
            setFileUploadState(prev => ({
                ...prev,
                loading: false
            }));
        }
    };

    useEffect(() => {
        const handleAudioMasteringReady = (event: CustomEvent) => {
            const { output, sessionId } = event.detail;

            setFileUploadState(prev => ({
                ...prev,
                loading: false
            }));
            setMasteringJobId(null);
            setMasteredTrackUrl(output);
            setIsMasteringComplete(true);
            refreshCredits();

            if (output) {
                toast.success('Mastering completed! Your track is ready.');
            } else {
                toast.success('Mastering completed!');
            }
        };

        const handleAudioTranscribeChordsReady = (event: CustomEvent) => {
            const { chords, name, sessionId } = event.detail;

            setFileUploadState(prev => ({
                ...prev,
                loading: false
            }));
            setMasteringJobId(null);
            setChordTranscriptionResult(chords);
            setChordTranscriptionName(name);
            setIsChordTranscriptionComplete(true);
            refreshCredits();

            if (chords) {
                toast.success('Chord transcription completed! Your chords are ready.');
                loadChordData(chords);
            } else {
                toast.success('Chord transcription completed!');
            }
        };

        const handleAudioStemSeparationReady = (event: CustomEvent) => {
            const { name, sessionId, Bass, Keys, Wind, Drums, Piano, Vocals, Guitars, Strings } = event.detail;

            setFileUploadState(prev => ({
                ...prev,
                loading: false
            }));
            setMasteringJobId(null);
            setStemSeparationResult({
                Bass,
                Keys,
                Wind,
                Drums,
                Piano,
                Vocals,
                Guitars,
                Strings
            });
            setStemSeparationName(name);
            setIsStemSeparationComplete(true);
            refreshCredits();

            if (Bass || Keys || Wind || Drums || Piano || Vocals || Guitars || Strings) {
                toast.success('Stem separation completed! Your stems are ready.');
            } else {
                toast.success('Stem separation completed!');
            }
        };

        const handleAudioDrumStemsReady = (event: CustomEvent) => {
            const { name, sessionId, Kick, Snare, HiHat, Tom, Cymbal } = event.detail;

            setFileUploadState(prev => ({
                ...prev,
                loading: false
            }));
            setMasteringJobId(null);
            setDrumStemsResult({
                Kick,
                Snare,
                HiHat,
                Tom,
                Cymbal
            });
            setDrumStemsName(name);
            setIsDrumStemsComplete(true);
            refreshCredits();

            if (Kick || Snare || HiHat || Tom || Cymbal) {
                toast.success('Drum stems completed! Your drum stems are ready.');
            } else {
                toast.success('Drum stems completed!');
            }
        };

        const handleJobError = (event: CustomEvent) => {
            const { message, jobId: errorJobId } = event.detail;

            if (errorJobId === masteringJobId || !masteringJobId) {
                setFileUploadState(prev => ({
                    ...prev,
                    loading: false
                }));
                setMasteringJobId(null);
                toast.error(`Process failed: ${message}`);
            }
        };

        window.addEventListener('audioMasteringReady', handleAudioMasteringReady as EventListener);
        window.addEventListener('audioTranscribeChordsReady', handleAudioTranscribeChordsReady as EventListener);
        window.addEventListener('audioStemSeparationReady', handleAudioStemSeparationReady as EventListener);
        window.addEventListener('audioDrumStemsReady', handleAudioDrumStemsReady as EventListener);
        window.addEventListener('jobError', handleJobError as EventListener);

        return () => {
            window.removeEventListener('audioMasteringReady', handleAudioMasteringReady as EventListener);
            window.removeEventListener('audioTranscribeChordsReady', handleAudioTranscribeChordsReady as EventListener);
            window.removeEventListener('audioStemSeparationReady', handleAudioStemSeparationReady as EventListener);
            window.removeEventListener('audioDrumStemsReady', handleAudioDrumStemsReady as EventListener);
            window.removeEventListener('jobError', handleJobError as EventListener);
        };
    }, [masteringJobId]);

    useEffect(() => {
        if (masteringJobId && fileUploadState.loading) {
            const timeout = setTimeout(() => {
                toast.error('Mastering process is taking longer than expected. Please check your connection.');
            }, 300000);

            return () => clearTimeout(timeout);
        }
    }, [masteringJobId, fileUploadState.loading]);



    const handleResetMastering = () => {
        setFileUploadState({
            selectedFile: null,
            acknowledged: false,
            loading: false,
            projectId: null
        });
        setMasteringJobId(null);
        setMasteredTrackUrl(null);
        setIsMasteringComplete(false);
    };

    const handleResetChordTranscription = () => {
        setFileUploadState({
            selectedFile: null,
            acknowledged: false,
            loading: false,
            projectId: null
        });
        setMasteringJobId(null);
        setChordTranscriptionResult(null);
        setChordTranscriptionName(null);
        setIsChordTranscriptionComplete(false);
        setChordData(null);
    };

    const handleResetStemSeparation = () => {
        setFileUploadState({
            selectedFile: null,
            acknowledged: false,
            loading: false,
            projectId: null
        });
        setMasteringJobId(null);
        setStemSeparationResult(null);
        setStemSeparationName(null);
        setIsStemSeparationComplete(false);
    };

    const handleResetDrumStems = () => {
        setFileUploadState({
            selectedFile: null,
            acknowledged: false,
            loading: false,
            projectId: null
        });
        setMasteringJobId(null);
        setDrumStemsResult(null);
        setDrumStemsName(null);
        setIsDrumStemsComplete(false);
    };

    const [chordData, setChordData] = useState<string | null>(null);

    const loadChordData = async (chordsUrl: string) => {
        try {
            const response = await fetch(chordsUrl, {
                headers: {
                    'Authorization': getAuthHeadersWithoutCredentials().Authorization,
                },
            });

            if (response.ok) {
                const data = await response.json();

                if (Array.isArray(data)) {
                    const formattedChords = formatChordProgression(data);
                    setChordData(formattedChords);
                } else {
                    setChordData('Invalid chord data format');
                }
            } else {
                setChordData('Failed to load chord data');
            }
        } catch (error) {
            console.error('Error loading chord data:', error);
            setChordData('Error loading chord data');
        }
    };

    const formatChordProgression = (chordSegments: any[]): string => {
        if (!chordSegments || chordSegments.length === 0) {
            return 'No chords detected';
        }

        console.log('Raw chord segments:', chordSegments);

        const firstChord = chordSegments[0];
        let key = '';
        let progression = '';

        let firstChordSymbol = firstChord.chord_simple_pop ||
            firstChord.chord_basic_pop ||
            firstChord.chord_simple_jazz ||
            firstChord.chord_basic_jazz ||
            '?';

        console.log('First chord symbol:', firstChordSymbol);

        if (firstChordSymbol.includes('F#')) key = 'F#';
        else if (firstChordSymbol.includes('F')) key = 'F';
        else if (firstChordSymbol.includes('G')) key = 'G';
        else if (firstChordSymbol.includes('C')) key = 'C';
        else if (firstChordSymbol.includes('D')) key = 'D';
        else if (firstChordSymbol.includes('A')) key = 'A';
        else if (firstChordSymbol.includes('B')) key = 'B';
        else if (firstChordSymbol.includes('E')) key = 'E';
        else key = firstChordSymbol.replace(/[^A-G#]/g, '');

        console.log('Determined key:', key);

        const chords = chordSegments.map(segment => {
            return segment.chord_simple_pop ||
                segment.chord_basic_pop ||
                segment.chord_simple_jazz ||
                segment.chord_basic_jazz ||
                '?';
        });

        console.log('Extracted chords:', chords);

        progression = `${key} : ${chords.join(' | ')} |`;

        console.log('Final progression:', progression);

        return progression;
    };

    const handleDownload = () => {
        if (masteredTrackUrl) {
            const link = document.createElement('a');
            link.href = masteredTrackUrl;
            link.download = 'mastered-track.wav';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    if (selectedTool === 'mastering' || selectedTool === 'chord-detector' || selectedTool === 'instrument-isolation' || selectedTool === 'drum-isolation') {
        const isMastering = selectedTool === 'mastering';
        const isChordDetector = selectedTool === 'chord-detector';
        const isInstrumentIsolation = selectedTool === 'instrument-isolation';
        const isDrumIsolation = selectedTool === 'drum-isolation';

        console.log('Current tool:', selectedTool, 'isMastering:', isMastering, 'isChordDetector:', isChordDetector, 'isInstrumentIsolation:', isInstrumentIsolation, 'isDrumIsolation:', isDrumIsolation);

        if (isLoading) {
            return (
                <div className="flex items-center justify-center h-screen">
                    <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
                </div>
            );
        }

        if (!isAuthenticated) {
            return null;
        }

        return (
            <div className="h-screen bg-background flex flex-col">
                {/* Top Bar */ }
                <div className="w-full border-b border-border mt-[60px] md:mt-0 flex-shrink-0">
                    <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={ handleBackToTools }
                                className="p-2 hover:bg-accent rounded-lg transition-colors"
                            >
                                <ArrowLeft size={ 20 } className="text-muted-foreground" />
                            </button>
                            <div className="border flex gap-3 items-center border-border rounded-lg px-4 py-2">
                                <h1 className="text-lg font-semibold text-card-foreground">
                                    Audio Utilities
                                </h1>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <User size={ 16 } className="text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{ username }</span>
                            <div className="ml-2 px-3 py-1 rounded-full bg-muted text-sm font-medium text-card-foreground">
                                { typeof credits === 'number' ? `${credits} credits` : '...' }
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content - Scrollable */ }
                <div className="flex-1 overflow-y-auto">
                    <div className="max-w-4xl mx-auto px-8 py-12">
                        <div className="text-center mb-12">
                            <h1 className="text-5xl font-bold text-card-foreground mb-4">
                                { isMastering ? 'Master Your Track' : isChordDetector ? 'Chord Detector' : isInstrumentIsolation ? 'Instrument Isolation' : 'Drum Isolation' }
                            </h1>
                            <p className="text-xl text-muted-foreground">
                                { isMastering
                                    ? 'Drop an audio file here and get your professionally mastered track.'
                                    : isChordDetector
                                        ? 'Upload an audio file to detect and identify the chords in your music.'
                                        : isInstrumentIsolation
                                            ? 'Upload an audio file to separate it into individual instrument stems.'
                                            : 'Upload an audio file to extract individual drum stem components.'
                                }
                            </p>
                        </div>

                        { selectedTool === 'mastering' && !isMasteringComplete ? (
                            <FileUploadArea
                                fileUploadState={ fileUploadState }
                                onFileSelect={ handleFileSelect }
                                onDrop={ handleFileDrop }
                                onGenerate={ handleGenerate }
                                onAcknowledgedChange={ handleAcknowledgedChange }
                                acceptAudio={ true }
                                masteringJobId={ masteringJobId }
                                // isWebSocketConnected={ isWebSocketConnected }
                                buttonText="Master Track"
                            />
                        ) : selectedTool === 'chord-detector' && !isChordTranscriptionComplete ? (
                            <FileUploadArea
                                fileUploadState={ fileUploadState }
                                onFileSelect={ handleFileSelect }
                                onDrop={ handleFileDrop }
                                onGenerate={ handleGenerate }
                                onAcknowledgedChange={ handleAcknowledgedChange }
                                acceptAudio={ true }
                                masteringJobId={ masteringJobId }
                                // isWebSocketConnected={ isWebSocketConnected }
                                buttonText="Detect Chords"
                            />
                        ) : selectedTool === 'instrument-isolation' && !isStemSeparationComplete ? (
                            <FileUploadArea
                                fileUploadState={ fileUploadState }
                                onFileSelect={ handleFileSelect }
                                onDrop={ handleFileDrop }
                                onGenerate={ handleGenerate }
                                onAcknowledgedChange={ handleAcknowledgedChange }
                                acceptAudio={ true }
                                masteringJobId={ masteringJobId }
                                // isWebSocketConnected={ isWebSocketConnected }
                                buttonText="Separate Stems"
                            />
                        ) : selectedTool === 'drum-isolation' && !isDrumStemsComplete ? (
                            <FileUploadArea
                                fileUploadState={ fileUploadState }
                                onFileSelect={ handleFileSelect }
                                onDrop={ handleFileDrop }
                                onGenerate={ handleGenerate }
                                onAcknowledgedChange={ handleAcknowledgedChange }
                                acceptAudio={ true }
                                masteringJobId={ masteringJobId }
                                // isWebSocketConnected={ isWebSocketConnected }
                                buttonText="Isolate Drums"
                            />
                        ) : selectedTool === 'mastering' && isMasteringComplete ? (
                            <div className="mt-8 w-full max-w-full rounded-2xl p-8 text-center">
                                <div className="flex flex-col items-center justify-center gap-4">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={ 2 } d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>

                                    <div>
                                        <h3 className="text-xl font-semibold text-card-foreground mb-2">
                                            { isMastering ? 'Mastering Completed!' : isChordDetector ? 'Chord Detection Completed!' : isInstrumentIsolation ? 'Stem Separation Completed!' : 'Drum Isolation Completed!' }
                                        </h3>
                                        <p className="text-muted-foreground">
                                            { isMastering
                                                ? 'Your track has been successfully mastered and is ready to download.'
                                                : isChordDetector
                                                    ? 'The chords in your audio have been detected and are ready to view.'
                                                    : isInstrumentIsolation
                                                        ? 'Your audio has been separated into individual instrument stems.'
                                                        : 'Your drum tracks have been isolated and are ready to download.'
                                            }
                                        </p>
                                    </div>

                                    {/* Audio Player */ }
                                    <div className="w-full max-w-4xl">
                                        <div className="flex items-center justify-center gap-4 mb-4">
                                            <SoundSignalVisualizer
                                                audioUrl={ masteredTrackUrl || '' }
                                                barsCount={ 8 }
                                                showPlaybackControls={ true }
                                                showBpmControls={ false }
                                                showExtraControls={ true }
                                                controlsExtra={
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={ handleDownload }
                                                            className="btn-primary px-4 py-2 flex items-center gap-2"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                            Download
                                                        </button>
                                                        <button
                                                            onClick={ handleResetMastering }
                                                            className="btn-primary px-4 py-2 flex items-center gap-2"
                                                        >
                                                            <RotateCcw className="w-4 h-4" />
                                                            Start again
                                                        </button>
                                                    </div>
                                                }
                                            />


                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : selectedTool === 'chord-detector' && isChordTranscriptionComplete ? (
                            <div className="mt-8 w-full max-w-full rounded-2xl p-8 text-center">
                                <div className="flex flex-col items-center justify-center gap-4">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={ 2 } d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>

                                    <div>
                                        <h3 className="text-xl font-semibold text-card-foreground mb-2">
                                            Chord Detection Completed!
                                        </h3>
                                        <p className="text-muted-foreground">
                                            Your chord progression has been successfully detected.
                                        </p>
                                    </div>

                                    {/* Chord Results */ }
                                    <div className="w-full max-w-4xl bg-card rounded-xl shadow-md border border-border p-8">
                                        <div className="text-center">
                                            <h4 className="text-lg font-semibold text-card-foreground mb-4">
                                                { chordTranscriptionName ? `Chords for: ${chordTranscriptionName}` : 'Detected Chords' }
                                            </h4>
                                            <div className="bg-muted rounded-lg p-6 border border-border">
                                                { chordData ? (
                                                    <div className="text-left">
                                                        <div className="text-3xl font-mono text-card-foreground leading-relaxed font-bold">
                                                            { chordData }
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center text-muted-foreground">
                                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-muted mx-auto mb-2"></div>
                                                        Loading chord data...
                                                    </div>
                                                ) }
                                            </div>
                                            <div className="mt-6">
                                                <button
                                                    onClick={ handleResetChordTranscription }
                                                    className="btn-primary px-6 py-3 flex items-center gap-2 mx-auto"
                                                >
                                                    <RotateCcw className="w-4 h-4" />
                                                    Start again
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : selectedTool === 'instrument-isolation' && isStemSeparationComplete ? (
                            <div className="mt-8 w-full max-w-full rounded-2xl p-8 text-center">
                                <div className="flex flex-col items-center justify-center gap-4">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={ 2 } d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>

                                    <div>
                                        <h3 className="text-xl font-semibold text-card-foreground mb-2">
                                            Instrument Separation Completed!
                                        </h3>
                                        <p className="text-muted-foreground">
                                            Your audio has been successfully separated into individual instrument stems.
                                        </p>
                                    </div>

                                    {/* Instrument Stems */ }
                                    <div className="w-full max-w-4xl">
                                        <div className="grid grid-cols-1 gap-6">
                                            {/* Vocals Stem */ }
                                            { stemSeparationResult?.Vocals && (
                                                <div className="bg-card rounded-xl shadow-md border border-border p-6">
                                                    <div className="text-left">
                                                        <h4 className="text-lg font-semibold text-card-foreground mb-4">Vocals Stem</h4>
                                                        <div className="w-full">
                                                            <SoundSignalVisualizer
                                                                audioUrl={ stemSeparationResult.Vocals }
                                                                barsCount={ 8 }
                                                                showPlaybackControls={ true }
                                                                showBpmControls={ false }
                                                                showExtraControls={ true }
                                                                controlsExtra={
                                                                    <button
                                                                        onClick={ () => handleDownload() }
                                                                        className="btn-primary px-4 py-2 flex items-center gap-2"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                        Download
                                                                    </button>
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) }

                                            {/* Piano Stem */ }
                                            { stemSeparationResult?.Piano && (
                                                <div className="bg-card rounded-xl shadow-md border border-border p-6">
                                                    <div className="text-left">
                                                        <h4 className="text-lg font-semibold text-card-foreground mb-4">Piano Stem</h4>
                                                        <div className="w-full">
                                                            <SoundSignalVisualizer
                                                                audioUrl={ stemSeparationResult.Piano }
                                                                barsCount={ 8 }
                                                                showPlaybackControls={ true }
                                                                showBpmControls={ false }
                                                                showExtraControls={ true }
                                                                controlsExtra={
                                                                    <button
                                                                        onClick={ () => handleDownload() }
                                                                        className="btn-primary px-4 py-2 flex items-center gap-2"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                        Download
                                                                    </button>
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) }

                                            {/* Drums Stem */ }
                                            { stemSeparationResult?.Drums && (
                                                <div className="bg-card rounded-xl shadow-md border border-border p-6">
                                                    <div className="text-left">
                                                        <h4 className="text-lg font-semibold text-card-foreground mb-4">Drum Kit Stem</h4>
                                                        <div className="w-full">
                                                            <SoundSignalVisualizer
                                                                audioUrl={ stemSeparationResult.Drums }
                                                                barsCount={ 8 }
                                                                showPlaybackControls={ true }
                                                                showBpmControls={ false }
                                                                showExtraControls={ true }
                                                                controlsExtra={
                                                                    <button
                                                                        onClick={ () => handleDownload() }
                                                                        className="btn-primary px-4 py-2 flex items-center gap-2"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                        Download
                                                                    </button>
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) }

                                            {/* Bass Stem */ }
                                            { stemSeparationResult?.Bass && (
                                                <div className="bg-card rounded-xl shadow-md border border-border p-6">
                                                    <div className="text-left">
                                                        <h4 className="text-lg font-semibold text-card-foreground mb-4">Bass Stem</h4>
                                                        <div className="w-full">
                                                            <SoundSignalVisualizer
                                                                audioUrl={ stemSeparationResult.Bass }
                                                                barsCount={ 8 }
                                                                showPlaybackControls={ true }
                                                                showBpmControls={ false }
                                                                showExtraControls={ true }
                                                                controlsExtra={
                                                                    <button
                                                                        onClick={ () => handleDownload() }
                                                                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                        Download
                                                                    </button>
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) }

                                            {/* Guitars Track */ }
                                            { stemSeparationResult?.Guitars && (
                                                <div className="bg-card rounded-xl shadow-md border border-border p-6">
                                                    <div className="text-left">
                                                        <h4 className="text-lg font-semibold text-card-foreground mb-4">Guitars</h4>
                                                        <div className="w-full">
                                                            <SoundSignalVisualizer
                                                                audioUrl={ stemSeparationResult.Guitars }
                                                                barsCount={ 8 }
                                                                showPlaybackControls={ true }
                                                                showBpmControls={ false }
                                                                showExtraControls={ true }
                                                                controlsExtra={
                                                                    <button
                                                                        onClick={ () => handleDownload() }
                                                                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                        Download
                                                                    </button>
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) }

                                            {/* Keys Track */ }
                                            { stemSeparationResult?.Keys && (
                                                <div className="bg-card rounded-xl shadow-md border border-border p-6">
                                                    <div className="text-left">
                                                        <h4 className="text-lg font-semibold text-card-foreground mb-4">Keys</h4>
                                                        <div className="w-full">
                                                            <SoundSignalVisualizer
                                                                audioUrl={ stemSeparationResult.Keys }
                                                                barsCount={ 8 }
                                                                showPlaybackControls={ true }
                                                                showBpmControls={ false }
                                                                showExtraControls={ true }
                                                                controlsExtra={
                                                                    <button
                                                                        onClick={ () => handleDownload() }
                                                                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                        Download
                                                                    </button>
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) }

                                            {/* Strings Track */ }
                                            { stemSeparationResult?.Strings && (
                                                <div className="bg-card rounded-xl shadow-md border border-border p-6">
                                                    <div className="text-left">
                                                        <h4 className="text-lg font-semibold text-card-foreground mb-4">Strings</h4>
                                                        <div className="w-full">
                                                            <SoundSignalVisualizer
                                                                audioUrl={ stemSeparationResult.Strings }
                                                                barsCount={ 8 }
                                                                showPlaybackControls={ true }
                                                                showBpmControls={ false }
                                                                showExtraControls={ true }
                                                                controlsExtra={
                                                                    <button
                                                                        onClick={ () => handleDownload() }
                                                                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                        Download
                                                                    </button>
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) }

                                            {/* Wind Track */ }
                                            { stemSeparationResult?.Wind && (
                                                <div className="bg-card rounded-xl shadow-md border border-border p-6">
                                                    <div className="text-left">
                                                        <h4 className="text-lg font-semibold text-card-foreground mb-4">Wind</h4>
                                                        <div className="w-full">
                                                            <SoundSignalVisualizer
                                                                audioUrl={ stemSeparationResult.Wind }
                                                                barsCount={ 8 }
                                                                showPlaybackControls={ true }
                                                                showBpmControls={ false }
                                                                showExtraControls={ true }
                                                                controlsExtra={
                                                                    <button
                                                                        onClick={ () => handleDownload() }
                                                                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                        Download
                                                                    </button>
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) }
                                        </div>

                                        <div className="mt-8">
                                            <button
                                                onClick={ handleResetStemSeparation }
                                                className="btn-primary px-6 py-3 flex items-center gap-2 mx-auto"
                                            >
                                                <RotateCcw className="w-4 h-4" />
                                                Start again
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : selectedTool === 'drum-isolation' && isDrumStemsComplete ? (
                            <div className="mt-8 w-full max-w-full rounded-2xl p-8 text-center">
                                <div className="flex flex-col items-center justify-center gap-4">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={ 2 } d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>

                                    <div>
                                        <h3 className="text-xl font-semibold text-card-foreground mb-2">
                                            Drum Stems Completed!
                                        </h3>
                                        <p className="text-muted-foreground">
                                            Your drum stems have been successfully separated.
                                        </p>
                                    </div>

                                    {/* Drum Component Stems */ }
                                    <div className="w-full max-w-4xl">
                                        <div className="grid grid-cols-1 gap-6">
                                            {/* Kick Drum Stem */ }
                                            { drumStemsResult?.Kick && (
                                                <div className="bg-card rounded-xl shadow-md border border-border p-6">
                                                    <div className="text-left">
                                                        <h4 className="text-lg font-semibold text-card-foreground mb-4">Kick Drum Stem</h4>
                                                        <div className="w-full">
                                                            <SoundSignalVisualizer
                                                                audioUrl={ drumStemsResult.Kick }
                                                                barsCount={ 8 }
                                                                showPlaybackControls={ true }
                                                                showBpmControls={ false }
                                                                showExtraControls={ true }
                                                                controlsExtra={
                                                                    <button
                                                                        onClick={ () => handleDownload() }
                                                                        className="btn-primary px-4 py-2 flex items-center gap-2"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                        Download
                                                                    </button>
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) }

                                            {/* Snare Drum Stem */ }
                                            { drumStemsResult?.Snare && (
                                                <div className="bg-card rounded-xl shadow-md border border-border p-6">
                                                    <div className="text-left">
                                                        <h4 className="text-lg font-semibold text-card-foreground mb-4">Snare Drum Stem</h4>
                                                        <div className="w-full">
                                                            <SoundSignalVisualizer
                                                                audioUrl={ drumStemsResult.Snare }
                                                                barsCount={ 8 }
                                                                showPlaybackControls={ true }
                                                                showBpmControls={ false }
                                                                showExtraControls={ true }
                                                                controlsExtra={
                                                                    <button
                                                                        onClick={ () => handleDownload() }
                                                                        className="btn-primary px-4 py-2 flex items-center gap-2"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                        Download
                                                                    </button>
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) }

                                            {/* HiHat Cymbal Stem */ }
                                            { drumStemsResult?.HiHat && (
                                                <div className="bg-card rounded-xl shadow-md border border-border p-6">
                                                    <div className="text-left">
                                                        <h4 className="text-lg font-semibold text-card-foreground mb-4">HiHat Cymbal Stem</h4>
                                                        <div className="w-full">
                                                            <SoundSignalVisualizer
                                                                audioUrl={ drumStemsResult.HiHat }
                                                                barsCount={ 8 }
                                                                showPlaybackControls={ true }
                                                                showBpmControls={ false }
                                                                showExtraControls={ true }
                                                                controlsExtra={
                                                                    <button
                                                                        onClick={ () => handleDownload() }
                                                                        className="btn-primary px-4 py-2 flex items-center gap-2"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                        Download
                                                                    </button>
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) }

                                            {/* Tom Drum Stem */ }
                                            { drumStemsResult?.Tom && (
                                                <div className="bg-card rounded-xl shadow-md border border-border p-6">
                                                    <div className="text-left">
                                                        <h4 className="text-lg font-semibold text-card-foreground mb-4">Tom Drum Stem</h4>
                                                        <div className="w-full">
                                                            <SoundSignalVisualizer
                                                                audioUrl={ drumStemsResult.Tom }
                                                                barsCount={ 8 }
                                                                showPlaybackControls={ true }
                                                                showBpmControls={ false }
                                                                showExtraControls={ true }
                                                                controlsExtra={
                                                                    <button
                                                                        onClick={ () => handleDownload() }
                                                                        className="btn-primary px-4 py-2 flex items-center gap-2"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                        Download
                                                                    </button>
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) }

                                            {/* Cymbal Stem */ }
                                            { drumStemsResult?.Cymbal && (
                                                <div className="bg-card rounded-xl shadow-md border border-border p-6">
                                                    <div className="text-left">
                                                        <h4 className="text-lg font-semibold text-card-foreground mb-4">Cymbal Stem</h4>
                                                        <div className="w-full">
                                                            <SoundSignalVisualizer
                                                                audioUrl={ drumStemsResult.Cymbal }
                                                                barsCount={ 8 }
                                                                showPlaybackControls={ true }
                                                                showBpmControls={ false }
                                                                showExtraControls={ true }
                                                                controlsExtra={
                                                                    <button
                                                                        onClick={ () => handleDownload() }
                                                                        className="btn-primary px-4 py-2 flex items-center gap-2"
                                                                    >
                                                                        <Download className="w-4 h-4" />
                                                                        Download
                                                                    </button>
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ) }
                                        </div>

                                        <div className="mt-8">
                                            <button
                                                onClick={ handleResetDrumStems }
                                                className="btn-primary px-6 py-3 flex items-center gap-2 mx-auto"
                                            >
                                                <RotateCcw className="w-4 h-4" />
                                                Start again
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null }
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen bg-background flex flex-col">
            {/* Top Bar */ }
            <div className="w-full border-b border-border mt-[60px] md:mt-0 flex-shrink-0">
                <div className="flex items-center justify-between px-4 py-3">
                    <div>
                        <div className="border flex gap-3 items-center border-border rounded-lg px-4 py-2">
                            <h1 className="text-lg font-semibold text-card-foreground">
                                Audio Utilities
                            </h1>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <User size={ 16 } className="text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{ username }</span>
                        <div className="ml-2 px-3 py-1 rounded-full bg-muted text-sm font-medium text-card-foreground">
                            { typeof credits === 'number' ? `${credits} credits` : '...' }
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content - Scrollable */ }
            <div className="flex-1 overflow-y-auto">
                <div className="max-w-6xl mx-auto px-8 py-12">
                    {/* Header */ }
                    <div className="text-center mb-16">
                        <h1 className="text-3xl font-bold text-card-foreground mb-4">
                            Essential audio tools
                        </h1>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            From mastering to stem separation, everything you need to finish your tracks
                        </p>
                    </div>

                    {/* Tools Grid */ }
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        { audioTools.map((tool) => (
                            <div
                                key={ tool.id }
                                className="bg-card rounded-xl p-6 shadow-sm border border-border hover:shadow-md hover:bg-accent transition-all duration-200 cursor-pointer"
                                onClick={ () => handleToolSelect(tool.id) }
                            >
                                {/* Icon */ }
                                <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mb-4 text-muted-foreground">
                                    { tool.icon }
                                </div>

                                {/* Title */ }
                                <h3 className="text-lg font-semibold text-card-foreground mb-3">
                                    { tool.title }
                                </h3>

                                {/* Description */ }
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    { tool.description }
                                </p>
                            </div>
                        )) }
                    </div>
                </div>
            </div>
        </div>
    );
};
